import classNames from 'classnames';
import { MdSave, MdUpload } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { Icon as RSIcon } from 'rsuite';
import { ButtonPrimary, FilterSection, LinkButton, PageLayout, TableHeading } from '../../../../../components';
import { parseStringToPlural } from '../../../../../utils';
import History from '../../../../../utils/History';
import { canAccessIpaNegotiation } from '../../../../../utils/ipaAccess';
import { DataUltimaIntegracao, Icon } from '../../../RevisaoPrecos/Components';
import { SalvarNegociacaoModal } from '../../components';
import { AdicionarVerbaEmMassaModal } from '../DetalhesDaNegociacao/components';
import { resolveWritingBarActions } from './acoesPorVisao';
import { CHAVE_DO_MODAL_DA_MASSA } from './verbaEmMassa';
import { useNegociacaoFornecedor } from './NegociacaoFornecedor.hooks';
import styles from './NegociacaoFornecedor.module.scss';
import * as T from './NegociacaoFornecedor.types';
import {
  AdicionarProdutoNegociacaoModal,
  BulkAdicionarVerbaButton,
  CriarNegociacaoArquivoModal,
  ErroProcessamentoArquivoModal,
  ProcessandoArquivoModal,
  SuppliersExhibitionHeader,
  TableFornecedor,
} from './components';

export const NegociacaoFornecedor = ({ className, usuario, ...props }: T.NegociacaoFornecedorProps) => {
  const {
    table: {
      data: { totalElements, content },
      selectedDatapoints,
      ...table
    },
    quantidadeSelecionada,
    modals,
    handlers: { openModal },
    filters,
    handlers: { download, isDownloadLoading },
  } = useNegociacaoFornecedor();

  // A disponibilidade das três ações que gravam não depende mais do agrupamento exibido, e é por isso
  // que esta tela deixou de ler o seletor "Agrupar por": a única condição que restou é haver ou não
  // seleção. A decisão em si continua fora daqui, no módulo irmão que tem teste próprio.
  const writingBarActions = resolveWritingBarActions({
    isSelectionEmpty: !!selectedDatapoints.isEmpty,
  });

  if (!usuario) return null;

  if (!canAccessIpaNegotiation(usuario)) {
    History.push('/ipa/gerenciador-de-precos');
    return null;
  }

  return (
    <>
      <PageLayout className={classNames(styles['negociacao-fornecedor'], className)} {...props}>
        <PageLayout.Header className={styles['negociacao-fornecedor__header']}>
          <div className={styles['negociacao-fornecedor__header-top-container']}>
            <div className={styles['negociacao-fornecedor__header-title-container']}>
              <PageLayout.Title>Negociação fornecedor</PageLayout.Title>
              <DataUltimaIntegracao variant="negotiations" />
            </div>
            <div className={styles['negociacao-fornecedor__header-actions']}>
              <ButtonPrimary theme="ghost" skin="blue" size="small" onClick={() => openModal('criarNegociacaoArquivo')} className={styles['btn-criar-arquivo']}>
                <MdUpload />
                Criar negociação com arquivo
              </ButtonPrimary>
              <SuppliersExhibitionHeader />
            </div>
          </div>

          <div>
            <PageLayout.Subtitle>Negocie o melhor custo para o seu produto</PageLayout.Subtitle>
            <LinkButton className={styles['link-button']} asChild>
              <Link to="/ipa/negociacoes/salvas">
                <MdSave />
                Abrir negociações salvas
              </Link>
            </LinkButton>
          </div>
        </PageLayout.Header>
        <PageLayout.Section>
          <FilterSection {...filters} className={styles['negociacao-fornecedor__filters']} />
        </PageLayout.Section>
        <TableHeading isSelected={!selectedDatapoints.isEmpty}>
          {/* A barra de contagem sai da tela quando a consulta que carrega a grade está em falha. Se o
              corpo da tabela não carregou, nada acima dele afirma quantidade — é o mesmo tratamento
              que a paginação já recebe na falha, e é decisão de produto de 14/08/2026 tomada por Cruj.

              A condição é o estado de falha da consulta, e NADA mais: sem condicional por agrupamento,
              pela mesma razão que fechou o alcance do bloco de erro nas três visões da grade. O defeito
              não é da visão rede — foi medido também na visão loja.

              O que ela deixava na tela era pior do que um número velho: com a seleção limpa e nenhuma
              linha carregada, a barra TROCAVA de frase e passava a afirmar seleção, com o realce de
              estado selecionado. Aquilo tinha duas causas, e ocultar a barra fecha só uma. A outra é a
              seleção se declarar cheia sobre uma carga que não existe, e ela foi consertada na origem,
              no gancho de seleção: o pedido de desligar o "selecionar tudo" invertia em vez de
              desligar, e portanto ligava. Era ela que deixava o botão de baixar acionável.

              As três ações que a barra sempre desenha continuam DESENHADAS, no bloco abaixo, e chegam
              indisponíveis por não haver seleção. Indisponível não é o mesmo que ausente: sumir com
              elas mudaria o desenho da barra num estado transitório. Some a afirmação de quantidade,
              não a barra de ações. O botão da verba é a exceção, e por regra própria: ele só existe
              com seleção, na falha e fora dela.

              No estado SEM RESULTADO a barra continua na tela dizendo zero resultado. Ali ela informa
              corretamente, e informar não é o defeito. */}
          {!table.isError && (
            <TableHeading.Container>
              <p>
                {!selectedDatapoints.isEmpty ? (
                  <strong>
                    {`${quantidadeSelecionada} produtos
                    ${parseStringToPlural('selecionado', quantidadeSelecionada)}`}
                  </strong>
                ) : (
                  <>
                    Foram encontrados
                    <strong>{` ${totalElements} ${parseStringToPlural('resultado', totalElements)} `}</strong>
                    no total
                  </>
                )}
              </p>
              {!filters.isEmpty && !selectedDatapoints.isEmpty ? (
                <LinkButton onClick={() => selectedDatapoints.onSelectAll()}>
                  {selectedDatapoints.data.selectedAll ? 'Limpar seleção' : 'Selecionar tudo'}
                </LinkButton>
              ) : null}
            </TableHeading.Container>
          )}
          <TableHeading.Container>
            {/* É o MESMO botão e o MESMO modal que a negociação salva usa, e não uma segunda versão
                deles: o gesto é o mesmo, e a tela de busca é onde o comprador monta a negociação.
                Só existe com seleção, porque só aí há a quem aplicar. */}
            {writingBarActions.addSellinAllowance.rendered && (
              <BulkAdicionarVerbaButton
                onClick={() => openModal(CHAVE_DO_MODAL_DA_MASSA)}
                disabled={writingBarActions.addSellinAllowance.disabled}
              />
            )}
            {/* O botão de baixar produtos selecionados sai desta entrega EXATAMENTE como entrou:
                nenhuma condição de agrupamento foi acrescentada a ele, e a regra do módulo irmão não
                o menciona. Não é esquecimento — é a decisão de 13/08/2026, tomada por Cruj, de que ele
                permanece na visão rede visível e acionável, com o conteúdo atual. A recomendação de
                deixá-lo indisponível foi recusada nominalmente, para manter esta entrega dentro da
                fronteira de uma decisão anterior de 09/08. Existe incompatibilidade conhecida no
                caminho dele, e ela está registrada como item pendente com dono — não como defeito
                desta entrega. */}
            <TableHeading.SecondaryButton
              icon={<Icon icon="MdFileDownload" />}
              onClick={download}
              placeholder="Baixar produtos selecionados"
              disabled={selectedDatapoints.isEmpty || isDownloadLoading}
              isLoading={isDownloadLoading}
            />
            {writingBarActions.addToSavedNegotiation.rendered && (
              <TableHeading.SecondaryButton
                icon={<RSIcon icon="plus" />}
                placeholder="Adicionar à negociação salva"
                disabled={writingBarActions.addToSavedNegotiation.disabled}
                onClick={() => {
                  openModal('adicionarProdutoNegociacao');
                }}
              />
            )}
            <TableHeading.PrimaryButton
              onClick={() => openModal('salvarNegociacao')}
              disabled={writingBarActions.save.disabled}
              id="save-negotiation-btn"
            >
              Salvar
            </TableHeading.PrimaryButton>
          </TableHeading.Container>
        </TableHeading>
        <TableFornecedor {...table} selectedDatapoints={selectedDatapoints} data={content} />
      </PageLayout>
      <AdicionarProdutoNegociacaoModal {...modals.adicionarProdutoNegociacao} />

      <AdicionarVerbaEmMassaModal {...modals.adicionarVerbaEmMassa} />

      <SalvarNegociacaoModal {...modals.salvarNegociacao} />

      <CriarNegociacaoArquivoModal {...modals.criarNegociacaoArquivo} />

      <ProcessandoArquivoModal {...modals.processandoArquivo} />

      <ErroProcessamentoArquivoModal {...modals.erroProcessamentoArquivo} />
    </>
  );
};
